import React, { FormEvent, useEffect } from 'react'
import { useForm, usePage } from '@inertiajs/react'
import { useReturnTo, appendReturnTo } from '@/contexts/ReturnToContext'
import * as yup from 'yup'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import Box from '@mui/material/Box'
import FormControl from '@mui/material/FormControl'
import FormHelperText from '@mui/material/FormHelperText'
import InputLabel from '@mui/material/InputLabel'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'

interface AgentRef {
  id: number
  username: string
  slug: string
}

interface BoardRef {
  id: number
  name: string
  slug: string
  agents?: AgentRef[]
}

interface Props {
  open: boolean
  onClose: () => void
  parentTaskId: number
  currentBoardSlug: string
  accessibleBoards: BoardRef[]
}

const requiredId = (label: string) =>
  yup.number().transform((value, original) => (original === '' ? undefined : value)).required(`${label} is required`)

const schema = yup.object({
  task: yup.object({
    summary: yup.string().required('Summary is required').max(255, 'Summary must be at most 255 characters'),
    instructions: yup.string().nullable().max(50000, 'Instructions must be at most 50,000 characters'),
    assignee_id: yup.mixed().nullable(),
    requestor_id: requiredId('Requestor'),
    reviewer_id: requiredId('Reviewer'),
  })
})

export function SubTaskFormDialog({ open, onClose, parentTaskId, currentBoardSlug, accessibleBoards }: Props) {
  const returnTo = useReturnTo()
  const { current_agent } = usePage().props as { current_agent?: { id: number } }

  const { data, setData, post, processing, errors, setError, clearErrors, reset } = useForm({
    board_slug: currentBoardSlug,
    task: {
      summary: '',
      instructions: '',
      assignee_id: '' as number | '',
      requestor_id: current_agent?.id ?? ('' as number | ''),
      reviewer_id: current_agent?.id ?? ('' as number | ''),
      parent_id: parentTaskId,
    },
  })

  const selectedBoard = accessibleBoards.find(b => b.slug === data.board_slug)
  const agents = selectedBoard?.agents ?? []

  useEffect(() => {
    if (!open) return
    setData({
      board_slug: currentBoardSlug,
      task: {
        summary: '',
        instructions: '',
        assignee_id: '',
        requestor_id: current_agent?.id ?? '',
        reviewer_id: current_agent?.id ?? '',
        parent_id: parentTaskId,
      },
    })
  }, [open])

  function handleBoardChange(newSlug: string) {
    setData({
      ...data,
      board_slug: newSlug,
      task: {
        ...data.task,
        assignee_id: '',
        requestor_id: current_agent?.id ?? '',
        reviewer_id: current_agent?.id ?? '',
      },
    })
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    clearErrors()

    try {
      schema.validateSync({ task: data.task }, { abortEarly: false })
    } catch (err) {
      if (err instanceof yup.ValidationError) {
        const validationErrors: Record<string, string> = {}
        err.inner.forEach((e) => {
          if (e.path) validationErrors[e.path] = e.message
        })
        setError(validationErrors)
        return
      }
    }

    post(appendReturnTo(`/tasks/boards/${data.board_slug}/tasks`, returnTo), {
      onSuccess: () => {
        reset()
        onClose()
      },
    })
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <Box component="form" onSubmit={handleSubmit} noValidate>
        <DialogTitle>New sub-task</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 1, mb: 2.5 }}>
            <InputLabel>Board</InputLabel>
            <Select
              value={data.board_slug}
              label="Board"
              onChange={e => handleBoardChange(e.target.value)}
            >
              {accessibleBoards.map((b) => (
                <MenuItem key={b.slug} value={b.slug}>{b.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Summary"
            fullWidth
            autoFocus
            value={data.task.summary}
            onChange={e => setData('task', { ...data.task, summary: e.target.value })}
            error={'task.summary' in errors}
            helperText={(errors as Record<string, string>)['task.summary']}
            sx={{ mb: 2.5 }}
          />
          <TextField
            label="Instructions"
            fullWidth
            multiline
            rows={4}
            value={data.task.instructions}
            onChange={e => setData('task', { ...data.task, instructions: e.target.value })}
            error={'task.instructions' in errors}
            helperText={(errors as Record<string, string>)['task.instructions']}
            sx={{ mb: 2.5 }}
          />
          <FormControl fullWidth sx={{ mb: 2.5 }}>
            <InputLabel>Assignee (optional)</InputLabel>
            <Select
              value={data.task.assignee_id}
              label="Assignee (optional)"
              onChange={e => setData('task', { ...data.task, assignee_id: e.target.value as number | '' })}
            >
              <MenuItem value="">Unassigned</MenuItem>
              {agents.map((a) => (
                <MenuItem key={a.id} value={a.id}>{a.username}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth sx={{ mb: 2.5 }} error={'task.requestor_id' in errors}>
            <InputLabel>Requestor</InputLabel>
            <Select
              value={data.task.requestor_id}
              label="Requestor"
              onChange={e => setData('task', { ...data.task, requestor_id: e.target.value as number })}
            >
              {agents.map((a) => (
                <MenuItem key={a.id} value={a.id}>{a.username}</MenuItem>
              ))}
            </Select>
            {'task.requestor_id' in errors && (
              <FormHelperText>{(errors as Record<string, string>)['task.requestor_id']}</FormHelperText>
            )}
          </FormControl>
          <FormControl fullWidth sx={{ mb: 2.5 }} error={'task.reviewer_id' in errors}>
            <InputLabel>Reviewer</InputLabel>
            <Select
              value={data.task.reviewer_id}
              label="Reviewer"
              onChange={e => setData('task', { ...data.task, reviewer_id: e.target.value as number })}
            >
              {agents.map((a) => (
                <MenuItem key={a.id} value={a.id}>{a.username}</MenuItem>
              ))}
            </Select>
            {'task.reviewer_id' in errors && (
              <FormHelperText>{(errors as Record<string, string>)['task.reviewer_id']}</FormHelperText>
            )}
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={processing}>
            Create sub-task
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  )
}
